import dayjs from "dayjs";

import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useSearchParams } from "react-router-dom";

import { Dialog, DialogContent, DialogTitle, LinearProgress, Typography } from "@mui/material";

import { getDealsFlight } from "@/api";
import CashFareRange from "@/components/CashFareRange";
import FilterBar from "@/components/FilterBar";
import FlightList from "@/components/FlightList";
import FlightListSkeleton from "@/components/FlightList/Skeleton";
import IntroFirstClass from "@/components/IntroFirstClass";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import Sort from "@/components/Sort";
import { sortFn } from "@/components/Sort/sortConfig";
import { RoundTripTypeEnum } from "@/constants";
import { ROUTES_PAGE_INIT_LOADDATA } from "@/constants/eventName";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { FlightItem as FlightItemType } from "@/typings";

import MobileSearchBar from "../DealsRoute/components/MobileSearchBar";
import SearchBar from "../DealsRoute/components/SearchBar";
import useCheckCondition from "../DealsRoute/hooks/useCheckCondition";
import { createSearchQuery } from "../DealsRoute/hooks/useCondition";
import { ConditionType } from "../DealsRoute/hooks/useCondition";
import { LocationEnum } from "../DealsRoute/hooks/useLocationFilter";
import { initialSort } from "./initialSort";

const FlightSearch = () => {
  const { isMobile } = usePlatform();
  const [, setSearchParams] = useSearchParams();
  const { checkCondition } = useCheckCondition();
  const theme = useThemeData();

  const [isLoading, setLoading] = useState(true);

  const [condition, setCondition] = useState<ConditionType>();
  const [searchData, setSearchData] = useState<FlightItemType[]>([]);
  const [filterData, setFilterData] = useState<FlightItemType[]>([]);
  const [sortKey, setSortKey] = useState(91);

  const [dialogOpen, setDialogOpen] = useState(false);

  const store = useGlobalStoreContext();
  const showUpsell = store.show_upsell;

  useListenEvent(ROUTES_PAGE_INIT_LOADDATA, (data: ConditionType) => {
    if (data.fromLocation.length !== 0 && data.toLocation.length !== 0) {
      setCondition(data);
      handleSearch(data);
    }
  });

  const appendSearchData = (data: FlightItemType[]) => {
    setSearchData(initialSort(data));
  };

  const handleSearch = useEvent(async (data: ConditionType) => {
    if (data) {
      const errMsg = checkCondition(data);

      if (errMsg) {
        setLoading(false);
        return;
      }

      const nextParams = createSearchQuery({ ...data });
      const nextSearch = createSearchParams(nextParams).toString();

      setSearchParams(nextSearch);
      setCondition(data);

      const fromLocation = {
        regions: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };
      const toLocation = {
        regions: data.toLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.toLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.toLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };

      const params = {
        date_range: {
          from: dayjs(data.startDate).format("YYYY-MM-DD"),
          to: dayjs(data.endDate).format("YYYY-MM-DD"),
        },
        origins: fromLocation,
        arrivals: toLocation,
      };

      try {
        await setLoading(true);
        await setSearchData([]);
        await getDealsFlight(params, appendSearchData, setDialogOpen, setLoading, showUpsell);
      } catch (error) {
        setLoading(false);
      }
    }
  });

  const filterChange = useCallback(
    (v: FlightItemType[]) => {
      const sortFilterData = sortFn(v, sortKey);
      setFilterData(sortFilterData);
    },
    [sortKey]
  );

  const sortChange = useCallback(
    (sortKey: number) => {
      const sortFilterData = sortFn(filterData, sortKey);
      setFilterData(sortFilterData);
      setSortKey(sortKey);
    },
    [filterData]
  );

  const [totalTitle, setTotalTitle] = useState("");

  useEffect(() => {
    let text = "";
    if (searchData.length > 0) {
      if (filterData.length === searchData.length) {
        text = `${searchData.length.toLocaleString()}` + " flights";
      } else {
        text =
          `${filterData.length.toLocaleString()}/${searchData.length.toLocaleString()}` +
          " flights";
      }
    }
    // FIXME: 延迟更新，防止闪烁，暂时没有想到更优的方式
    const timer = setTimeout(() => {
      setTotalTitle(text);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [searchData, filterData]);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | First-class Flights</title>
        <meta
          name="description"
          content="Make your journey unforgettable with an exclusive first-class flight experience."
        />
      </Helmet>
      <div className="max-w-6xl mx-auto">
        {isMobile ? (
          <>
            <MobileSearchBar data={condition} onSearch={handleSearch} isLoading={isLoading} />
            <div className="px-[10px] mt-[16px]">
              <FilterBar
                dataSourceList={searchData}
                onChange={filterChange}
                roundTripType={RoundTripTypeEnum.Outbound}
              />
              <div className="mt-[16px]">
                <IntroFirstClass />
              </div>
            </div>
          </>
        ) : (
          <div className="px-[10px]">
            <div className="pt-[16px]">
              <SearchBar onSearch={handleSearch} isLoading={isLoading} />
            </div>

            <div className="pb-[5px]">
              <FilterBar
                dataSourceList={searchData}
                onChange={filterChange}
                roundTripType={RoundTripTypeEnum.Outbound}
              />
            </div>

            <IntroFirstClass />
          </div>
        )}

        {isLoading ? (
          <div className="mt-[9px] mx-[10px]">
            <LinearProgress />
          </div>
        ) : null}
        {filterData.length === 0 && !isLoading ? (
          <div className="mt-[50px] text-center">
            <Typography color="textSecondary">No options matching your filters</Typography>
            <Typography color="textSecondary">
              Consider modify your filter setting or search criteria
            </Typography>
            <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
          </div>
        ) : (
          <div className="px-[10px] mt-[-5px]">
            {filterData.length ? (
              <>
                <div className="flex items-center justify-between pt-[10px] pb-[10px]">
                  <Typography variant="body1" color="textPrimary">
                    {totalTitle}
                  </Typography>
                  <div className="flex items-center">
                    <CashFareRange filterList={filterData} sourceList={searchData} />
                    <Sort value={sortKey} onChange={sortChange} />
                  </div>
                </div>

                <FlightList data={filterData} />
              </>
            ) : null}

            {isLoading ? <FlightListSkeleton count={filterData.length} /> : null}
          </div>
        )}
      </div>

      <Dialog
        onClose={() => {
          setDialogOpen(false);
        }}
        open={dialogOpen}
        sx={{
          maxWidth: "750px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle>
          <Typography color="textPrimary">Search Condition Error</Typography>
        </DialogTitle>
        <DialogContent dividers className="pt-[20px] !px-[20px]">
          Search condition is wrong, please check your search condition.
        </DialogContent>
      </Dialog>

      <ScrollTopBar />
    </>
  );
};

export default FlightSearch;
